import React, { FunctionComponent } from 'react'
import { Button, Text } from '@afterpaytouch/core'
import { MaskedElement } from '@afterpaytouch/integrations/MaskedElement'
import { useRoutePageName } from '../../config/router'
import { CHECKOUT_FLOW_TYPE, postCheckoutFlowMessage } from '../../integrations/inappbrowser-messenger'
import { useMonitoringWithEnduringEventProperties } from '../../integrations/monitoring'
import { useGiftCardRecipient } from '../../state'
import { useTranslation } from '../../utils/convergence'
import { SectionHeader } from '../SectionHeader'
import styles from './style.module.css'

export const GIFT_CARD_RECIPIENT_NAMESPACE = 'gift-card-recipient'

export const GiftCardRecipientSection: FunctionComponent = () => {
  const { t } = useTranslation(['common', 'summary'])
  const pageName = useRoutePageName()
  const { logEvent } = useMonitoringWithEnduringEventProperties()
  const recipient = useGiftCardRecipient()

  if (recipient === null) {
    return null
  }

  const { name, email, senderName, message } = recipient

  const handleEdit = (): void => {
    logEvent(`Pressed Edit Gift Card Recipient on ${pageName} Page`)
    postCheckoutFlowMessage(CHECKOUT_FLOW_TYPE.ON_GIFT_CARD_CLOSE)
  }

  return (
    <section className={styles.section} data-testid={GIFT_CARD_RECIPIENT_NAMESPACE}>
      <SectionHeader title={t('summary:giftCard.recipient.title', { context: 'default' })} />
      <div className={styles.row}>
        <MaskedElement actionName='Gift card recipient' testNameSpace={`${GIFT_CARD_RECIPIENT_NAMESPACE}-details`}>
          <div className={styles.details}>
            {name !== null && (
              <Text size='M' bold renderAs='div' testNameSpace={`${GIFT_CARD_RECIPIENT_NAMESPACE}-name`}>
                {name}
              </Text>
            )}
            {email !== null && (
              <Text size='S' color='Subtle' renderAs='div' testNameSpace={`${GIFT_CARD_RECIPIENT_NAMESPACE}-email`}>
                {email}
              </Text>
            )}
          </div>
        </MaskedElement>
        <Button
          type='button'
          kind='Secondary'
          size='Small'
          aria-label={t('summary:giftCard.recipient.editLabel', { context: 'default' })}
          testNameSpace={`${GIFT_CARD_RECIPIENT_NAMESPACE}-edit`}
          onClick={handleEdit}
        >
          {t('common:edit')}
        </Button>
      </div>
      {message !== null && (
        <MaskedElement actionName='Gift card message' testNameSpace={`${GIFT_CARD_RECIPIENT_NAMESPACE}-masked-message`}>
          <div className={styles.message}>
            {senderName !== null && (
              <Text size='S' color='Subtle' renderAs='div' testNameSpace={`${GIFT_CARD_RECIPIENT_NAMESPACE}-sender`}>
                {t('summary:giftCard.recipient.from', { context: 'default', senderName, interpolation: { escapeValue: false } })}
              </Text>
            )}
            <Text size='S' color='Subtle' renderAs='div' testNameSpace={`${GIFT_CARD_RECIPIENT_NAMESPACE}-message`}>
              {t('summary:giftCard.recipient.message', { context: 'default', message, interpolation: { escapeValue: false } })}
            </Text>
          </div>
        </MaskedElement>
      )}
    </section>
  )
}
